import { Suspense, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { Navigate, RouterProvider } from 'react-router';
import { createBrowserRouter, useRouteError } from 'react-router-dom';
import { useSearchParam } from 'react-use';

import { ProgressBar } from '@/components/ProgressBar';
import { AuthGuard } from '@/guards/AuthGuard';
import { GuestGuard } from '@/guards/GuestGuard';
import { NecessityFileGuard } from '@/guards/NecessityFileGuard';
import { SignUpGuard } from '@/guards/SignUpGuard';
import { InviteSignUpGuard } from '@/guards/SignUpGuard/InviteSignUpGuard';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AutoSelectionLayout } from '@/layouts/AutoSelectionLayout';
import { CloudCartRootLayout } from '@/layouts/CloudCartRootLayout';
import { MainLayout } from '@/layouts/MainLayout';
import { ProductSelectionLayout } from '@/layouts/ProductSelectionLayout';
import { ProfileLayout } from '@/layouts/ProfileLayout';
import { ReferralLayout } from '@/layouts/ReferralLayout';
import { SettingsLayout } from '@/layouts/Settings';
import { AccountPage } from '@/pages/auth/reset-password/AccountPage';
import { BanPage } from '@/pages/auth/reset-password/BanPage';
import { NewPasswordPage } from '@/pages/auth/reset-password/NewPasswordPage';
import { SuccessPage } from '@/pages/auth/reset-password/SuccessPage';
import { VerifyPhonePage as ResetPasswordVerifyPhoneForm } from '@/pages/auth/reset-password/VerifyPhonePage';
import SignInPage from '@/pages/auth/sign-in';
import { CompanyForm } from '@/pages/auth/sign-up/forms/CompanyForm';
import { CreatePasswordForm } from '@/pages/auth/sign-up/forms/CreatePasswordForm';
import { InviteCreatePasswordForm } from '@/pages/auth/sign-up/forms/CreatePasswordForm/InviteCreatePasswordForm';
import { PharmacyForm } from '@/pages/auth/sign-up/forms/PharmacyForm';
import { RegistrationForm } from '@/pages/auth/sign-up/forms/RegistrationForm';
import { InviteRegistrationForm } from '@/pages/auth/sign-up/forms/RegistrationForm/InviteRegistrationForm';
import { VerifyPhoneForm as SignUpVerifyPhoneForm } from '@/pages/auth/sign-up/forms/VerifyPhoneForm';
import { InviteVerifyPhoneForm } from '@/pages/auth/sign-up/forms/VerifyPhoneForm/InviteVerifyPhoneForm';
import { SignUpBanPage } from '@/pages/auth/sign-up/SignUpBanPage';
import { SignUpFinalPage } from '@/pages/auth/sign-up/SignUpFinalPage';
import { useSignUpStoreActions } from '@/pages/auth/sign-up/store/signUp.store';
import CreateTelegramGroupPage from '@/pages/create-telegram-group';
import { DorimFinanceRoute } from '@/pages/dorim-finance';
import { DorimPromoRoute } from '@/pages/dorim-promo';
import OrdersPage from '@/pages/orders';
import OrderViewPage from '@/pages/orders/order-view';
import { ReconciliationBindingPage } from '@/pages/orders/reconciliation/binding';
import { ReconciliationBindingBreadcrumb } from '@/pages/orders/reconciliation/binding/ReconciliationBindingBreadcrumb';
import { ReconciliationFieldMatchingPage } from '@/pages/orders/reconciliation/field-matching';
import { ReconciliationResultPage } from '@/pages/orders/reconciliation/result';
import {
  NecessityListFieldMatchingPage,
  OrderRoute,
  ProductSelectionAutoPage,
  ProductSelectionAutoSettingsPage,
  ProductSelectionBestOffersPage,
  ProductSelectionBoundDrugsPage,
  ProductSelectionByDistributorPage,
  ProductSelectionManualPage,
  ProductSelectionSpecialOffersPage,
} from '@/pages/product-selection';
import { ProductSelectionAutoBindingPage } from '@/pages/product-selection/auto/binding';
import { EntityInfoPage, PersonalInfoPage } from '@/pages/profile';
import ReferralRequestCompletedPage from '@/pages/referral/referral-request-completed/ReferralRequestCompletedPage';
import ReferralPage from '@/pages/referral/ReferralPage';
import { RequirementCalculationRoute } from '@/pages/requirements-calculation';
import {
  DistributorsSettingsPage,
  ExpirationDatesSettingsPage,
  PharmacySettingsPage,
  UsersSettingsPage,
} from '@/pages/settings';
import {
  AuthPaths,
  BreadcrumbLink,
  CreateTelegramGroupPaths,
  OrdersPaths,
  PriceListsPaths,
  ProductSelectionPaths,
  ProfilePaths,
  ReferralPaths,
  SettingsPaths,
  SpecialPriceListsPaths,
} from '@/shared/lib/react-router';

import { Providers } from './Providers';
import { Root } from './Root';

/**
 * @link https://github.com/remix-run/react-router/discussions/10166
 */
const BubbleError = () => {
  const error = useRouteError();

  if (error) throw error;
  return null;
};

const router = createBrowserRouter([
  {
    element: (
      <Providers>
        <Root />
      </Providers>
    ),
    errorElement: <BubbleError />,
    children: [
      {
        path: ReferralPaths.ROOT_PATH,
        element: (
          <GuestGuard>
            <ReferralLayout type="sign-in" />
          </GuestGuard>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <ReferralPage />
              </Suspense>
            ),
          },
          {
            path: ReferralPaths.REFERRAL_REQUEST_COMPLETED_PATH,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <ReferralRequestCompletedPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: AuthPaths.SIGN_IN.ROOT,
        element: (
          <GuestGuard>
            <AuthLayout type="sign-in" />
          </GuestGuard>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignInPage />
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_IN.BAN,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpBanPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: AuthPaths.RESET_PASSWORD.ROOT,
        element: (
          <GuestGuard>
            <AuthLayout type="reset-password" />
          </GuestGuard>
        ),
        children: [
          { index: true, element: <Navigate replace to={AuthPaths.RESET_PASSWORD.ACCOUNT} /> },
          {
            path: AuthPaths.RESET_PASSWORD.ACCOUNT,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <AccountPage />
              </Suspense>
            ),
          },
          {
            path: AuthPaths.RESET_PASSWORD.VERIFY_PHONE,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <ResetPasswordVerifyPhoneForm />
              </Suspense>
            ),
          },
          {
            path: AuthPaths.RESET_PASSWORD.NEW_PASSWORD,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <NewPasswordPage />
              </Suspense>
            ),
          },
          {
            path: AuthPaths.RESET_PASSWORD.SUCCESS,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SuccessPage />
              </Suspense>
            ),
          },
          {
            path: AuthPaths.RESET_PASSWORD.BAN,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <BanPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: AuthPaths.SIGN_UP_INVITE.ROOT,
        element: <AuthLayout type="sign-up" />,
        children: [
          {
            index: true,
            Component: () => {
              const { setUserData } = useSignUpStoreActions();
              const inviteToken = useSearchParam('invite_token') || '';

              useEffect(() => {
                if (inviteToken) {
                  setUserData({ inviteToken });
                }
              }, [inviteToken, setUserData]);

              return <Navigate replace to={AuthPaths.SIGN_UP_INVITE.DEFAULT} />;
            },
          },
          {
            path: AuthPaths.SIGN_UP_INVITE.REGISTRATION,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <InviteSignUpGuard>
                  <InviteRegistrationForm />
                </InviteSignUpGuard>
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP_INVITE.VERIFY_PHONE,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <InviteSignUpGuard>
                  <InviteVerifyPhoneForm />
                </InviteSignUpGuard>
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP_INVITE.CREATE_PASSWORD,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <InviteSignUpGuard>
                  <InviteCreatePasswordForm />
                </InviteSignUpGuard>
              </Suspense>
            ),
          },
          { path: '*', element: <Navigate replace to={AuthPaths.SIGN_UP_INVITE.DEFAULT} /> },
        ],
      },
      {
        path: AuthPaths.SIGN_UP.ROOT,
        element: <AuthLayout type="sign-up" />,
        children: [
          { index: true, element: <Navigate replace to={AuthPaths.SIGN_UP.DEFAULT} /> },
          {
            path: AuthPaths.SIGN_UP.REGISTRATION,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpGuard>
                  <RegistrationForm />
                </SignUpGuard>
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP.VERIFY_PHONE,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpGuard>
                  <SignUpVerifyPhoneForm />
                </SignUpGuard>
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP.CREATE_PASSWORD,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpGuard>
                  <CreatePasswordForm />
                </SignUpGuard>
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP.COMPANY_INFO,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpGuard>
                  <CompanyForm />
                </SignUpGuard>
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP.PHARMACY_INFO,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpGuard>
                  <PharmacyForm />
                </SignUpGuard>
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP.BAN,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpBanPage />
              </Suspense>
            ),
          },
          {
            path: AuthPaths.SIGN_UP.FINAL,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <SignUpFinalPage />
              </Suspense>
            ),
          },
          { path: '*', element: <Navigate replace to={AuthPaths.SIGN_UP.DEFAULT} /> },
        ],
      },

      { path: AuthPaths.SIGN_UP.OLD_ROOT, element: <Navigate replace to={AuthPaths.SIGN_UP.ROOT} /> },

      { path: '/', element: <Navigate replace to={ProductSelectionPaths.MANUAL_SELECTION_PATH} /> },
      RequirementCalculationRoute,
      {
        path: ProductSelectionPaths.ROOT_PATH,
        element: (
          <AuthGuard>
            <CloudCartRootLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: ProductSelectionPaths.ROOT_PATH,
            element: <ProductSelectionLayout />,
            children: [
              {
                index: true,
                element: <Navigate replace to={ProductSelectionPaths.MANUAL_SELECTION_PATH} />,
              },
              {
                path: ProductSelectionPaths.MANUAL_SELECTION_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionManualPage />
                  </Suspense>
                ),
              },
              {
                path: ProductSelectionPaths.SPECIAL_OFFERS_SELECTION_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionSpecialOffersPage />
                  </Suspense>
                ),
              },
              {
                path: ProductSelectionPaths.BY_DISTRIBUTOR_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionByDistributorPage />
                  </Suspense>
                ),
              },
              {
                path: ProductSelectionPaths.AUTO_SELECTION_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionAutoPage />
                  </Suspense>
                ),
              },
              {
                path: ProductSelectionPaths.AUTO_SELECTION_SETTINGS_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionAutoSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: ProductSelectionPaths.BOUND_DRUGS_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionBoundDrugsPage />
                  </Suspense>
                ),
              },
              {
                path: ProductSelectionPaths.BEST_OFFERS_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionBestOffersPage />
                  </Suspense>
                ),
              },
            ],
          },

          OrderRoute,

          {
            path: ProductSelectionPaths.AUTO_SELECTION_PATH,
            element: (
              <NecessityFileGuard>
                <AutoSelectionLayout />
              </NecessityFileGuard>
            ),
            children: [
              {
                path: ProductSelectionPaths.FIELD_MATCHING_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <NecessityListFieldMatchingPage />
                  </Suspense>
                ),
              },
              {
                path: ProductSelectionPaths.BINDING_PATH,
                element: (
                  <Suspense fallback={<ProgressBar />}>
                    <ProductSelectionAutoBindingPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },

      {
        path: OrdersPaths.ROOT,
        element: (
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        ),
        handle: {
          breadcrumb: {
            element: (
              <BreadcrumbLink to={OrdersPaths.ROOT}>
                <Trans ns="orders-page" i18nKey="title" />
              </BreadcrumbLink>
            ),
          },
        },
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <OrdersPage />
              </Suspense>
            ),
          },
          {
            path: OrdersPaths.RECONCILIATION_FIELD_MATCHING,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <ReconciliationFieldMatchingPage />
              </Suspense>
            ),
          },
          {
            path: OrdersPaths.VIEW,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <OrderViewPage />
              </Suspense>
            ),
          },
          {
            path: OrdersPaths.RECONCILIATION_BINDING,
            handle: {
              breadcrumb: {
                Component: ReconciliationBindingBreadcrumb,
              },
            },
            element: (
              <Suspense fallback={<ProgressBar />}>
                <ReconciliationBindingPage />
              </Suspense>
            ),
          },
          {
            path: OrdersPaths.RECONCILIATION_RESULT,
            handle: {
              breadcrumb: {
                element: (
                  <BreadcrumbLink to={OrdersPaths.RECONCILIATION_RESULT}>
                    <Trans ns="orders-page" i18nKey="reconciliation.result.page-title" />
                  </BreadcrumbLink>
                ),
              },
            },
            element: (
              <Suspense fallback={<ProgressBar />}>
                <ReconciliationResultPage />
              </Suspense>
            ),
          },
        ],
      },

      {
        path: PriceListsPaths.ROOT,
        element: (
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        ),
        children: [
          {
            handle: {
              breadcrumb: {
                element: (
                  <BreadcrumbLink to={PriceListsPaths.ROOT}>
                    <Trans ns="price-lists-page" i18nKey="page.title" />
                  </BreadcrumbLink>
                ),
              },
            },
            index: true,
            lazy: async () => {
              const { default: PriceListsPage } = await import(
                /* webpackChunkName: "PriceListsPage" */
                '@/pages/price-lists/PriceListsPage'
              );
              return {
                Component: () => (
                  <Suspense fallback={<ProgressBar />}>
                    <PriceListsPage />
                  </Suspense>
                ),
              };
            },
          },
        ],
      },

      {
        path: SpecialPriceListsPaths.ROOT,
        element: (
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        ),
        children: [
          {
            handle: {
              breadcrumb: {
                element: (
                  <BreadcrumbLink to={SpecialPriceListsPaths.ROOT}>
                    <Trans ns="special-price-lists-page" i18nKey="page.title" />
                  </BreadcrumbLink>
                ),
              },
            },
            index: true,
            lazy: async () => {
              const { default: SpecialPriceListsPage } = await import(
                /* webpackChunkName: "SpecialPriceListsPage" */
                '@/pages/special-price-lists/SpecialPriceListsPage'
              );
              return {
                Component: () => (
                  <Suspense fallback={<ProgressBar />}>
                    <SpecialPriceListsPage />
                  </Suspense>
                ),
              };
            },
          },
        ],
      },

      DorimFinanceRoute,
      DorimPromoRoute,

      {
        path: ProfilePaths.ROOT_PATH,
        element: (
          <AuthGuard>
            <ProfileLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <Navigate replace to={ProfilePaths.PERSONAL_INFO_PATH} />,
          },
          {
            path: ProfilePaths.PERSONAL_INFO_PATH,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <PersonalInfoPage />
              </Suspense>
            ),
          },
          {
            path: ProfilePaths.ENTITY_INFO_PATH,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <EntityInfoPage />
              </Suspense>
            ),
          },
        ],
      },

      {
        path: SettingsPaths.ROOT,
        element: (
          <AuthGuard>
            <Suspense fallback={<ProgressBar />}>
              <SettingsLayout />
            </Suspense>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate replace to={SettingsPaths.DISTRIBUTORS} /> },
          {
            path: SettingsPaths.DISTRIBUTORS,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <DistributorsSettingsPage />
              </Suspense>
            ),
          },
          {
            path: SettingsPaths.EXPIRY_DATES,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <ExpirationDatesSettingsPage />
              </Suspense>
            ),
          },
          {
            path: SettingsPaths.PHARMACIES,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <PharmacySettingsPage />
              </Suspense>
            ),
          },
          {
            path: SettingsPaths.USERS,
            element: (
              <Suspense fallback={<ProgressBar />}>
                <UsersSettingsPage />
              </Suspense>
            ),
          },
        ],
      },

      {
        path: CreateTelegramGroupPaths.ROOT,
        element: <CreateTelegramGroupPage />,
      },

      {
        path: '*',
        element: <Navigate replace to="/" />,
      },
    ],
  },
]);

export const Router = () => <RouterProvider router={router} />;
